import { useRef, useEffect, useCallback } from 'react';
import { readConversation, writeConversation } from '../../services/ai/conversationStore';
import { readCachedConversation, writeCachedConversation, persistToken, isPersistTokenLive } from '../../services/ai/conversationCache';
import { buildPersistSnapshot } from '../../services/ai/conversationSnapshot';
import { CONVERSATION_SAVE_DEBOUNCE_MS } from './aiChatConstants';

export function useConversationPersistence({
  currentWorkspaceId,
  messages,
  isStreaming,
  setMessages,
  anthropicHistoryRef,
  inFlightHistoryRef,
  threadRef,
  confirmResolveRef,
  streamFinishRef,
}) {
  const persistStateRef = useRef(null);
  persistStateRef.current = { messages, isStreaming };

  // ── Conversation persistence ───────────────────────────────────────────────
  //
  // The panel is unmounted whenever it closes (App renders it behind
  // `aiPanelOpen &&`), so without this the thread dies on close, not merely on
  // restart. Restore runs per workspace: restoring one workspace's thread into
  // another is a data-leak-shaped bug in a product with teams.
  const conversationRestoredRef = useRef(false);
  // The thread's OWN identity: the workspace it belongs to and the persistence
  // token of the session that produced it. Every writer keys by these, never
  // by the current prop — a workspace switch or a sign-out between "produced"
  // and "written" must not file it under the wrong key, or at all.

  /** Persist the thread this instance holds, under ITS id, if its session is live. */
  const flushThread = useCallback(() => {
    const { workspaceId, token } = threadRef.current;
    const { messages: msgs, isStreaming: streaming } = persistStateRef.current;
    if (!workspaceId || !conversationRestoredRef.current || !msgs.length) return;
    if (!isPersistTokenLive(token)) return;
    const snapshot = buildPersistSnapshot({
      messages: msgs, isStreaming: streaming, history: anthropicHistoryRef.current, inFlightHistory: inFlightHistoryRef.current,
    });
    writeCachedConversation(workspaceId, snapshot);
    writeConversation(workspaceId, snapshot);
  }, [threadRef, persistStateRef, anthropicHistoryRef, inFlightHistoryRef]);

  useEffect(() => {
    if (!currentWorkspaceId) return undefined;
    let cancelled = false;
    conversationRestoredRef.current = false;
    // This instance now holds THIS workspace's thread. Start from nothing: the
    // previous workspace's transcript was flushed under its own id by the
    // cleanup below and must not be shown — or saved — here.
    threadRef.current = { workspaceId: currentWorkspaceId, token: persistToken() };
    setMessages([]);
    anthropicHistoryRef.current = [];

    // Cache first: a panel closed and reopened in the same session gets its
    // thread back synchronously, and a disk copy — older, debounced, Tauri-only
    // — must not overwrite it. Disk is the fresh-launch path only.
    const cached = readCachedConversation(currentWorkspaceId);
    if (cached?.messages?.length) {
      setMessages(cached.messages);
      anthropicHistoryRef.current = cached.anthropicHistory || [];
      conversationRestoredRef.current = true;
    } else {
      readConversation(currentWorkspaceId).then((saved) => {
        if (cancelled) return;
        if (saved?.messages?.length) {
          setMessages(saved.messages);
          // BOTH shapes, or the assistant has amnesia about its own tool calls:
          // `messages` is what the transcript renders, `anthropicHistory` is what
          // the model sees, and the second holds the tool_use/tool_result blocks
          // the first renders as cards.
          anthropicHistoryRef.current = saved.anthropicHistory || [];
        }
        // Set LAST, and set even when there was nothing to restore: the save
        // effect below is gated on it, and an empty `messages` written before the
        // read resolves would erase the very thread being restored.
        conversationRestoredRef.current = true;
      });
    }

    // Cleanup = this workspace's thread is going away: the panel closed (App
    // unmounts it) or the workspace changed. Flush what exists to BOTH stores
    // under this workspace's id. The debounced write below only clears its
    // timer, so a close inside the quiet period (or mid-reply, when it does
    // not schedule) used to lose the tail on disk too. Fire-and-forget — the
    // promise outlives the instance. A confirm gate still open belongs to an
    // instance that will not exist: cancel it rather than persist a live card.
    return () => {
      cancelled = true;
      // Flush FIRST (the pending card is snapshotted as cancelled), then let
      // the in-flight turn settle: its loop sees a changed identity and exits.
      flushThread();
      if (confirmResolveRef.current) {
        const resolve = confirmResolveRef.current;
        confirmResolveRef.current = null;
        resolve(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps -- holds the in-flight turn's resolver (set by useAiStreamTurn), not a DOM node: read it at cleanup time
      streamFinishRef.current?.();
    };
  }, [currentWorkspaceId, flushThread, threadRef, setMessages, anthropicHistoryRef, confirmResolveRef, streamFinishRef]);

  useEffect(() => {
    if (isStreaming || !currentWorkspaceId || !conversationRestoredRef.current) return undefined;
    if (!messages.length) return undefined;
    // Debounced, and outside the streaming loop: a write per token would hammer
    // the disk for a file nothing reads until the next launch.
    const timer = setTimeout(() => {
      const { workspaceId, token } = threadRef.current;
      if (!isPersistTokenLive(token)) return;
      writeConversation(workspaceId, {
        messages,
        anthropicHistory: anthropicHistoryRef.current,
      });
    }, CONVERSATION_SAVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [isStreaming, messages, currentWorkspaceId, threadRef, anthropicHistoryRef]);

  // The in-memory copy is written on every change (a Map set — cheap), so the
  // thread survives the panel closing regardless of the debounce, of a reply
  // being mid-stream, or of disk being unavailable (non-Tauri).
  useEffect(() => {
    // Empty is never written: clearSession deletes the entry itself, and a
    // freshly-mounted empty panel must not shadow a thread restored later.
    if (!currentWorkspaceId || !conversationRestoredRef.current || !messages.length) return;
    const { workspaceId, token } = threadRef.current;
    if (!isPersistTokenLive(token)) return;
    writeCachedConversation(workspaceId, buildPersistSnapshot({
      messages, isStreaming, history: anthropicHistoryRef.current, inFlightHistory: inFlightHistoryRef.current,
    }));
  }, [isStreaming, messages, currentWorkspaceId, threadRef, anthropicHistoryRef, inFlightHistoryRef]);
}
